import { Injectable, inject } from '@angular/core';
import { Observable, tap } from 'rxjs';
import { S3UploadApi, FilesState } from '@shared';
import { UploadToS3Command } from './upload-to-s3.command';

/**
 * Use case for uploading a file to S3 using a presigned URL.
 * Updates state with upload progress.
 */
@Injectable({ providedIn: 'root' })
export class UploadToS3UseCase {
  private readonly _s3Api = inject(S3UploadApi);
  private readonly _state = inject(FilesState);

  /**
   * Executes the use case to upload a file to S3.
   *
   * @param command - Command containing presigned URL and file
   * @returns Observable that completes when upload finishes
   */
  public execute(command: UploadToS3Command): Observable<void> {
    this._state.startUpload(command.fileId);

    const onProgress = (progress: number) => {
      this._state.updateProgress(command.fileId, progress);
      command.onProgress?.(progress);
    };

    return this._s3Api.uploadToS3(command.presignedUrl, command.file, command.requiredHeaders ?? {}, onProgress).pipe(
      tap({
        error: (error: unknown) => {
          const errorMessage = error instanceof Error ? error.message : 'Upload failed';
          this._state.failUpload(command.fileId, errorMessage);
        },
      })
    );
  }
}
